//#region imports

// react
import React, { useState, useEffect, useMemo, use } from 'react';
import { Button } from 'react-bootstrap';
import { app, firestore } from "_utils/firebase-app";
import { collection, onSnapshot, getDocs } from "firebase/firestore";
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';

// redux
import { useSelector, useDispatch } from 'react-redux';
import { selectFirebaseInfo } from "_redux/slices/main-screen/user";
import { getAirportUuidAndCoordinates, SelectCurrentCoordinates } from "_redux/slices/timeline/timeline-live-location-tab";
import { SelectTenant } from "_redux/slices/timeline/timeline-live-location-tab";
import { getBeaconList, selectBeaconList } from "_redux/slices/asset-tracking/asset-tracking";

// custom components
import Sidebar from './sidebar';
import AssetTrackingMap from './asset-tracking-map';
import AssetTrackingLocation from "./asset-tracking-locations";

// styling
import { Colors, Spacing } from "_styles";
import './styles.css';
import { fi } from 'date-fns/locale';

//#endregion


const AssetTrackingTab = (props) => {

    //#region local states
    const [applications, setApplications] = useState({});
    const [activeApp, setActiveApp] = useState(null);
    const [devices, setDevices] = useState([]);
    const tenant = useSelector(SelectTenant);
    const [assetStateFilter, setAssetStateFilter] = useState(null);
    const [now, setNow] = useState(Date.now());
    //#endregion

    //#region redux
    const dispatch = useDispatch();
    const dbUser = useSelector(selectFirebaseInfo);
    const beaconList = useSelector(selectBeaconList);
    //#endregion

    //#region handlers

    // Fetch & listen to applications
    async function listenTenantApplications(tenantId) {
        const appsRef = collection(firestore, "tenants", tenantId, "applications");
        const appsSnap = await getDocs(appsRef);
        appsSnap.forEach((appDoc) => {
            const appName = appDoc.id;
            const devicesRef = collection(
                firestore,
                "tenants",
                tenantId,
                "applications",
                appName,
                "devices"
            );

            onSnapshot(devicesRef, (snapshot) => {
                const devices = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                setApplications(prev => ({
                    ...prev,
                    [appName]: devices
                }));
            });
        });
    }
    //#endregion

    //#region memos

    // filtered devices based on asset state
    const filteredDevices = useMemo(() => {
        const THIRTY_MINUTES = 30 * 60; // in seconds

        return devices.filter((device) => {

            const lastMotionTs = device?.meta?.deviceLastMotionAt;
            const stillInMotion = device?.latest?.stillInMotion === true;

            if (assetStateFilter == "Active") {

                if (!lastMotionTs) return false;

                const lastMotionTime = lastMotionTs.toDate();

                const timeDiffSeconds = (now - lastMotionTime) / 1000;

                const recentlyActive = timeDiffSeconds <= THIRTY_MINUTES;

                return recentlyActive || (stillInMotion && timeDiffSeconds <= THIRTY_MINUTES);
            }
            else if (assetStateFilter == "Idle") {

                if (!lastMotionTs) return true;

                const lastMotionTime = lastMotionTs.toDate();

                const timeDiffSeconds = (now - lastMotionTime) / 1000;

                const isIdle = timeDiffSeconds > THIRTY_MINUTES;

                return isIdle && !stillInMotion;
            }
            else {
                return true;
            }
        });

    }, [devices, assetStateFilter, applications, now]);

    //#endregion

    //#region useEffects

    // on load - get airport uuid and coordinates
    useEffect(() => {
        dispatch(getAirportUuidAndCoordinates(dbUser?.details?.authority));
    }, [dbUser]);

    // Initial load - listen to applications and fetch beacons
    useEffect(() => {
        if (tenant) {
            listenTenantApplications(tenant);
            if (dbUser?.details?.authority) {
                dispatch(getBeaconList({ iata: dbUser?.details?.authority }));
            }
        }
    }, [tenant]);

    // once applications are loaded, set the first application as active, and load its devices
    useEffect(() => {
        const appNames = Object.keys(applications);
        
        if (appNames.length > 0 && !activeApp) {
            const firstApp = appNames[0];
            setDevices(applications[firstApp] || []);
            setActiveApp(firstApp);
        }
        else if (activeApp && applications[activeApp]) {
            setDevices(applications[activeApp]);
        }
        console.log("Applications updated: ", applications);
    }, [applications]);

    // Update devices when application changes
    useEffect(() => {
        if (activeApp && applications[activeApp]) {
            setDevices(applications[activeApp]);
        }
    }, [activeApp]);

    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 60000);
        return () => clearInterval(id);
    }, []);
    //#endregion

    return (
        <div className="d-flex gap-1" style={{ height: '95vh', width: '100%', padding: Spacing.SCALE_4 }} id='assetTrackingDiv'>

            {/* sidebar */}
            <Sidebar applications={applications} activeApp={activeApp} setActiveApp={setActiveApp} devices={devices} assetStateFilter={assetStateFilter} setAssetStateFilter={setAssetStateFilter} />

            {/* main content - tabs */}
            <div className='border rounded p-3 bg-light' style={{ width: '100%', height: '100%' }}>
                <Tabs
                    defaultActiveKey="locationsTable"
                    id="asset-tracking-tabs"
                    className="mb-4"
                >
                    <Tab eventKey="locationsTable" title="Locations">
                        <AssetTrackingLocation beaconList={beaconList} devices={filteredDevices} />
                    </Tab>
                    <Tab eventKey="map" title="Map" >
                        <div style={{ height: '80vh', width: '100%' }}>
                            <AssetTrackingMap beaconList={beaconList} devices={filteredDevices} />
                        </div>
                    </Tab>
                </Tabs>
            </div>

        </div >
    );
};

export default AssetTrackingTab;
